// Shared by the runtime <Seo> and the build-time prerender so titles and structured data match in both.
const strip = (v) => (typeof v === "string" ? v.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim() : v);

export function shortenTitle(t, max = 60) {
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 2);
  const i = Math.max(cut.lastIndexOf(":"), cut.lastIndexOf(" "));
  return cut.slice(0, i > 30 ? i : max - 2).replace(/[\s:,;&|-]+$/, "");
}

export function shortenDesc(d, max = 158) {
  const t = String(d || "").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const i = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf(", "), cut.lastIndexOf(" "));
  return `${cut.slice(0, i > 80 ? i : max - 1).replace(/[\s,;:]+$/, "")}.`.replace(/\.\.$/, ".");
}

export function fitTitle(title, siteName = "Vistolane") {
  const t = String(title || "").trim();
  if (t.toLowerCase().includes(siteName.toLowerCase())) return t.length <= 60 ? t : shortenTitle(t.replace(new RegExp(`\\s*\\|\\s*${siteName}\\s*$`, "i"), ""));
  return t.length + siteName.length + 3 <= 60 ? `${t} | ${siteName}` : shortenTitle(t);
}

const publisher = (site, name) => ({ "@type": "Organization", name, url: site, logo: { "@type": "ImageObject", url: `${site}/assets/logo/vistolane-tile-512.png`, width: 512, height: 512 } });

// Fills the fields Google's Rich Results Test marks as required and drops nodes that would validate as errors.
export function normaliseNode(node, { site, name = "Vistolane", url, image, description }) {
  if (!node || typeof node !== "object" || !node["@type"]) return null;
  const n = { ...node };
  const today = new Date().toISOString().slice(0, 10);
  if (n["@type"] === "FAQPage") {
    const qa = (n.mainEntity || []).filter((q) => q?.name && q?.acceptedAnswer?.text).map((q) => ({ "@type": "Question", name: strip(q.name), acceptedAnswer: { "@type": "Answer", text: strip(q.acceptedAnswer.text) } }));
    return qa.length ? { ...n, mainEntity: qa } : null;
  }
  if (["Article", "NewsArticle", "BlogPosting"].includes(n["@type"])) {
    n.headline = strip(n.headline || "").slice(0, 110);
    if (!n.headline) return null;
    n.image = n.image ? (Array.isArray(n.image) ? n.image : [n.image]) : [image || `${site}/og-image.jpg`];
    n.datePublished = n.datePublished || n.dateModified || today;
    n.dateModified = n.dateModified || n.datePublished;
    n.author = n.author ? { url: site, ...n.author } : { "@type": "Organization", name, url: site };
    n.publisher = { ...publisher(site, name), ...(n.publisher || {}), logo: publisher(site, name).logo };
    n.mainEntityOfPage = n.mainEntityOfPage || url;
    if (!n.description && description) n.description = description;
  }
  if (n["@type"] === "HowTo") {
    n.step = (n.step || []).filter((st) => st?.name).map((st, i) => ({ "@type": "HowToStep", position: i + 1, name: strip(st.name), text: strip(st.text || st.name) }));
    if (!n.step.length) return null;
    if (n.estimatedCost && n.estimatedCost.value != null) n.estimatedCost = { "@type": "MonetaryAmount", currency: n.estimatedCost.currency || "USD", value: String(n.estimatedCost.value) }; else delete n.estimatedCost;
    if (!n.totalTime) delete n.totalTime;
  }
  if (n["@type"] === "BreadcrumbList") {
    n.itemListElement = (n.itemListElement || []).filter((it) => it?.name && it?.item).map((it, i) => ({ "@type": "ListItem", position: i + 1, name: strip(it.name), item: it.item }));
    if (!n.itemListElement.length) return null;
  }
  return n;
}

export const normaliseGraph = (graph, ctx) => (graph || []).map((n) => normaliseNode(n, ctx)).filter(Boolean);
